import { BalanceData, EventReward, PaidReward, RARITY_IDS, chestShare } from "@highmarch/domain";
import type { GearItem } from "@highmarch/domain";
import { fmtCompact } from "../kit";
import { icon } from "../iconsKit";
import { chestIconUrl, resourceIconUrl } from "../portraits";
import { fmtSpeedUp } from "./ordersSheet";
import { gemIcon, marksIcon, trophiesIcon } from "./econArt";
import { paintedIconHtml } from "../featureIcons";
import { emblemArtUrl } from "../../render/skins/emblemArt";
import { essenceIconHtml, essenceWords, sigilIconHtml, sigilWords } from "../forgeArt";
import { supplyChipHtml, supplyIconHtml, supplyName } from "../supplies/supplyArt";
import "../styles/trophies.css";

/**
 * How an event, Trial or Heroic reward reads (WP6): the painted chest, resource and feature icons (ui/featureIcons.ts,
 * painted in WP12) and the painted flag emblem. Presentation only: what a reward is worth comes from the domain
 * (meta/events.ts).
 */

const CHEST_NAMES = ["Common", "Uncommon", "Rare", "Epic", "Legendary", "Mythic"] as const;

/** A cut gem (the Shop's currency). */
export function gemGlyph(size = 18): string { return gemIcon(size); }

/** A Mark: the trade token. */
export function marksGlyph(size = 18): string { return marksIcon(size); }

/** Trophies (MP-23): the hardest weekly fights' prize, spent in the Trophy Hall. */
export function trophiesGlyph(size = 18): string { return trophiesIcon(size); }

/** "40 Trophies" / "1 Trophy". */
export function trophiesWords(n: number): string { return `${fmtCompact(n)} ${n === 1 ? "Trophy" : "Trophies"}`; }

/** An ember: the event currency. */
export function emberGlyph(size = 18): string { return paintedIconHtml("eventEmber", size, "glyph ember"); }

/** The flag emblem a track's last node unlocks (the Skins package applies it): its painted shield. */
export function emblemGlyph(size: number, id: string): string {
  return `<img class="glyph emblem" decoding="sync" src="${emblemArtUrl(id)}" width="${size}" height="${size}" alt="">`;
}

/** The emblem's display name ("Ashen banner"). */
export function emblemName(id: string): string { return `${id.charAt(0).toUpperCase()}${id.slice(1)} banner`; }

/** The forge goods in a reward (gear v2): sigils of one rarity, and essence. */
type ForgeGoods = Pick<EventReward, "essence" | "sigil" | "sigils">;
const sigilsOf = (r: ForgeGoods): number => (r.sigil === null ? 0 : Math.max(1, r.sigils));

/**
 * The one picture a node shows: its most notable part (the emblem, a chest, Fusion Sigils, gems, Marks, Forge Essence,
 * a speed-up, then supplies; Trophies only when they are all it pays: they show as their own tag).
 */
export function rewardArt(r: EventReward, size: number): string {
  if (r.emblem) return emblemGlyph(size, r.emblem);
  if (r.chest !== null) return `<img decoding="sync" src="${chestIconUrl(RARITY_IDS[r.chest])}" alt="" style="height:${size}px">`;
  if (r.sigil !== null) return sigilIconHtml(r.sigil, size, "glyph");
  if (r.gems > 0) return gemGlyph(size);
  if (r.marks > 0) return marksGlyph(size);
  if (r.essence > 0) return essenceIconHtml(size, "glyph");
  if (r.speedUpMinutes > 0) return icon("clock", size);
  if (r.supply) return supplyIconHtml(r.supply, size);
  // Trophies sit beside a prize as their own tag (trophyTagHtml); the picture is theirs only when nothing else is paid
  if (r.resources.isZero() && (r.trophies ?? 0) > 0) return trophiesGlyph(size);
  return `<img decoding="sync" src="${resourceIconUrl("gold")}" alt="" style="height:${size}px">`;
}

/**
 * What a reward's gold share (`resources`, in average Win chests) is worth at the Commander's level, for its words;
 * the events feature sets it at boot (M39b: "Supplies ×1.5" read as the new pre-battle supplies). Null: "Gold".
 */
let goldWorth: ((r: EventReward) => number) | null = null;
export function setRewardGoldWorth(fn: ((r: EventReward) => number) | null): void { goldWorth = fn; }
/** `chestShare` at the highest cleared level: the resolver the hub sets. */
export const goldWorthAt = (B: BalanceData, level: () => number) => (r: EventReward): number => chestShare(B, Math.max(1, level()), r.resources);

/** A reward in a few words ("Epic chest", "+250 Marks", "1h speed-up", "1,200 gold · 5 gems"); `trophies` false leaves them out. */
export function rewardWords(r: EventReward, trophies = true): string {
  const parts: string[] = [];
  if (r.emblem) parts.push(emblemName(r.emblem));
  if (r.chest !== null) parts.push(`${CHEST_NAMES[r.chest]} chest`);
  if (r.gems > 0) parts.push(`${r.gems} gems`);
  if (r.marks > 0) parts.push(`${fmtCompact(r.marks)} Marks`);
  if (trophies && (r.trophies ?? 0) > 0) parts.push(trophiesWords(r.trophies ?? 0));
  if (r.sigil !== null) parts.push(sigilWords(r.sigil, sigilsOf(r)));
  if (r.essence > 0) parts.push(essenceWords(r.essence));
  if (r.speedUpMinutes > 0) parts.push(`${fmtSpeedUp(r.speedUpMinutes)} speed-up`);
  if (!r.resources.isZero()) parts.push(goldWorth ? `${fmtCompact(goldWorth(r))} gold` : "Gold");
  if (r.supply) parts.push(`${(r.supplies ?? 1) > 1 ? `${r.supplies} × ` : ""}${supplyName(null, r.supply)}`);
  return parts.join(" · ");
}

/** A reward's forge goods as chips (essence, sigils): the claim rows of the milestone tracks and the rewards. */
export function forgeChipsHtml(r: ForgeGoods, px = 15, unit = false): string {
  const chips: string[] = [];
  if (r.sigil !== null) chips.push(`<span class="rw sig r${r.sigil}">${sigilIconHtml(r.sigil, px)}${sigilWords(r.sigil, sigilsOf(r)).replace(/^1 /, "")}</span>`);
  if (r.essence > 0) chips.push(`<span class="rw ess">${essenceIconHtml(px)}${fmtCompact(r.essence)}${unit ? ` <span class="u">essence</span>` : ""}</span>`);
  return chips.join("");
}

/** Trophies as a reward chip (MP-23): the painted token, the number, "Trophies". */
export function trophyChipHtml(n: number, px = 15): string {
  return `<span class="rw tro">${trophiesGlyph(px)}${fmtCompact(n)} <span class="u">${n === 1 ? "Trophy" : "Trophies"}</span></span>`;
}

/** A small inline Trophies tag (the token and the number) for a reward's caption: event nodes, mode prizes, ladders. */
export function trophyTagHtml(n: number, px = 14, plus = false): string {
  return n > 0 ? `<span class="trotag" aria-label="${trophiesWords(n)}">${trophiesGlyph(px)}${plus ? "+" : ""}${fmtCompact(n)}</span>` : "";
}

/** A reward's words with its Trophies as the painted tag (every reward UI that lists prizes: MP-23). */
export function rewardWordsHtml(r: EventReward, px = 14): string {
  const words = rewardWords(r, false), tag = trophyTagHtml(r.trophies ?? 0, px);
  return words && tag ? `${words} ${tag}` : words || tag;
}

/** A reward as chips (Trial card): gold sized for the player's level, then the rest. */
export function rewardChipsHtml(B: BalanceData, level: number, r: EventReward): string {
  const gold = r.resources.isZero() ? 0 : chestShare(B, Math.max(1, level), r.resources);
  const chips: string[] = [];
  if (gold > 0) chips.push(`<span class="rw"><img decoding="sync" src="${resourceIconUrl("gold")}" alt="gold">${fmtCompact(gold)} <span class="u">gold</span></span>`);
  // Every chip names what it pays (M32: a bare clock and "2h" read as nothing): the number, then its unit.
  if (r.marks > 0) chips.push(`<span class="rw">${marksGlyph(15)}${fmtCompact(r.marks)} <span class="u">Marks</span></span>`);
  if ((r.trophies ?? 0) > 0) chips.push(trophyChipHtml(r.trophies ?? 0, 15));
  if (r.gems > 0) chips.push(`<span class="rw">${gemGlyph(15)}${r.gems} <span class="u">${r.gems === 1 ? "gem" : "gems"}</span></span>`);
  if (r.speedUpMinutes > 0) chips.push(`<span class="rw su">${icon("clock", 13)}${fmtSpeedUp(r.speedUpMinutes)} <span class="u">speed-up</span></span>`);
  if (r.chest !== null) chips.push(`<span class="rw"><img decoding="sync" src="${chestIconUrl(RARITY_IDS[r.chest])}" alt="">${CHEST_NAMES[r.chest]} <span class="u">chest</span></span>`);
  if (r.emblem) chips.push(`<span class="rw">${emblemGlyph(15, r.emblem)}${emblemName(r.emblem)}</span>`);
  if (r.supply) chips.push(supplyChipHtml(B, r.supply, r.supplies ?? 1));
  return chips.join("") + forgeChipsHtml(r, 15, true);
}

/** What a paid reward gave, as one toast line; `trophies` false leaves them out (an end screen's own Trophies row says them). */
export function paidLine(p: PaidReward, gear: readonly GearItem[], trophies = true): string {
  const parts: string[] = [];
  if (p.gold > 0) parts.push(`+${fmtCompact(p.gold)} gold`);
  if (p.gems > 0) parts.push(`+${p.gems} gems`);
  if (p.marks > 0) parts.push(`+${fmtCompact(p.marks)} Marks`);
  if (trophies && (p.trophies ?? 0) > 0) parts.push(`+${trophiesWords(p.trophies ?? 0)}`);
  if (p.speedUpMinutes > 0) parts.push(`${fmtSpeedUp(p.speedUpMinutes)} speed-up`);
  if (p.chest) parts.push(`${CHEST_NAMES[p.chest.rarity]} chest${gear.length > 0 ? ` (${gear.length} gear)` : ""}`);
  if (p.emblem) parts.push(`${emblemName(p.emblem)} unlocked`);
  if (p.sigil !== null && p.sigils > 0) parts.push(`+${sigilWords(p.sigil, p.sigils)}`);
  if (p.essence > 0) parts.push(`+${essenceWords(p.essence)}`);
  if (p.supply && (p.supplies ?? 0) > 0) parts.push(`+${p.supplies} ${supplyName(null, p.supply)}`);
  return parts.join(" · ");
}
